import { FC, useEffect, useState } from 'react';

import { endOfDay, startOfDay } from 'date-fns';
import { useYearQuarterNavigation } from 'hooks';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';

import { PlannedHour } from 'components/types';

import { getBaseplans, getPlannedHours } from 'RestAPIs/capacity-plan/baseplan';

import { scenarioAtom } from '../atoms';

import PlannedHoursChart from './components/PlannedHoursChart';
import PlannedHoursDialog from './components/PlannedHoursDialog';
import PlannedHoursTable from './components/PlannedHoursTable';

import { queryKeys, useSaveAndUpdateMutation } from './queries';
import {
  GridContainer,
  GridCompact12
} from 'components/shared/Grid/Grid.components';

interface BaseplanTabProps {
  setRefreshTotalsGraph: (refresh: boolean) => void;
}

const BaseplanTab: FC<BaseplanTabProps> = ({ setRefreshTotalsGraph }) => {
  const scenario = useRecoilValue(scenarioAtom);

  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [plannedHourToEdit, setPlannedHourToEdit] = useState<Omit<
    PlannedHour,
    'scenarioId' | 'organizationalUnit' | 'endDate'
  > | null>();

  const { year } = useYearQuarterNavigation();

  const { data: plannedHours, isLoading: isLoadingPlannedHours } = useQuery(
    [queryKeys.BASEPLAN, queryKeys.PLANNED_HOURS, scenario?.scenarioId, year],
    async () => {
      if (!scenario?.scenarioId) {
        return Promise.reject(new Error('no scenario ID'));
      }

      const { data } = await getPlannedHours({
        scenarioId: scenario?.scenarioId,
        year
      });
      return data;
    },
    {
      enabled: Boolean(scenario?.scenarioId)
    }
  );

  const { data: baseplans, isLoading: isLoadingBasePlans } = useQuery(
    [queryKeys.BASEPLAN, queryKeys.BASEPLANS, scenario, year, minDate, maxDate],
    async () => {
      if (!scenario || !minDate || !maxDate) {
        return Promise.reject(new Error('Incorrect state'));
      }
      const { scenarioId, costCenters } = scenario;

      const { data } = await getBaseplans({
        scenarioId,
        startDate: minDate.toISOString(),
        endDate: maxDate.toISOString(),
        costCenters: costCenters.map(({ id }) => id).join(),
        includeArchived: true
      });
      return data;
    },
    {
      enabled: Boolean(minDate) && Boolean(maxDate) && Boolean(scenario)
    }
  );

  const { mutate: saveAndUpdate, isLoading: isSaving } =
    useSaveAndUpdateMutation();

  const isLoading = isLoadingPlannedHours || isLoadingBasePlans || isSaving;

  useEffect(() => {
    const startDate = startOfDay(new Date(year, 0, 1));
    const endDate = endOfDay(new Date(year, 11, 31));
    setMinDate(startDate);
    setMaxDate(endDate);
  }, [year]);

  const onAddClick = () => {
    setPlannedHourToEdit(null);
    setDialogOpen(true);
  };

  const onEditClick = (startDate: string) => {
    setDialogOpen(true);
    const plannedHour = plannedHours?.find(
      ({ startDate: _startDate }) => startDate === _startDate
    );

    if (plannedHour) {
      const newPlannedHourToEdit = {
        baseplanId: plannedHour.baseplanId,
        startDate: plannedHour.startDate
      };

      setPlannedHourToEdit(newPlannedHourToEdit);
    }
  };

  const onDeleteClick = (startDate: string) => {
    const payload = plannedHours?.filter(
      ({ startDate: _startDate }) => startDate !== _startDate
    );

    if (payload && scenario) {
      saveAndUpdate(
        {
          scenarioId: scenario?.scenarioId,
          year,
          payload
        },
        {
          onSuccess: () => {
            setRefreshTotalsGraph(true);
          }
        }
      );
    }
  };

  if (!scenario) {
    return null;
  }

  return (
    <GridContainer>
      {baseplans && plannedHours && plannedHours.length > 0 && (
        <GridCompact12 mb={2}>
          <PlannedHoursChart
            baseplans={baseplans}
            plannedHours={plannedHours}
            year={year}
          />
        </GridCompact12>
      )}
      {baseplans && plannedHours && (
        <GridCompact12>
          <PlannedHoursTable
            disableActions={scenario?.readOnly}
            year={year}
            plannedHours={plannedHours}
            baseplans={baseplans}
            onAddClick={onAddClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
          >
            <PlannedHoursDialog
              isOpen={dialogOpen}
              handleClose={() => setDialogOpen(false)}
              options={baseplans}
              plannedHours={plannedHours}
              editValues={plannedHourToEdit}
              minDate={minDate}
              maxDate={maxDate}
              loading={isLoading}
            />
          </PlannedHoursTable>
        </GridCompact12>
      )}
    </GridContainer>
  );
};

export default BaseplanTab;
